// src/pages/Public/About.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import logo from '../../assets/images/cac-im.jpeg';
import {
    AirIcon,
    SeaIcon,
    CustomsIcon,
    GroupageIcon,
    StoreIcon,
    TrackingIcon,
    PresenceIcon,
    SupportIcon,
    RealTimeIcon,
    SecurityIcon,
    SpeedIcon,
    PriceIcon,
    TrophyIcon,
    BoxIcon,
    GlobeIcon,
    StarIcon
} from '../../components/Icons/ServiceIcons';

// Données centralisées avec icônes SVG (plus d'emojis)
const statsData = [
    { value: '10+', label: "Années d'expérience", icon: <TrophyIcon />, color: '#1565C0', delay: 0 },
    { value: '5000+', label: 'Colis transportés', icon: <BoxIcon />, color: '#2E7D32', delay: 100 },
    { value: '5', label: 'Pays desservis', icon: <GlobeIcon />, color: '#F9A825', delay: 200 },
    { value: '98%', label: 'Satisfaction client', icon: <StarIcon />, color: '#C62828', delay: 300 }
];

const servicesData = [
    { title: 'Transport Aérien', desc: 'Expédition rapide par avion. Idéal pour les marchandises urgentes.', icon: <AirIcon />, color: '#1565C0' },
    { title: 'Transport Maritime', desc: 'Solution économique pour gros volumes par bateau.', icon: <SeaIcon />, color: '#2E7D32' },
    { title: 'Dédouanement', desc: 'Prise en charge complète des formalités douanières.', icon: <CustomsIcon />, color: '#F9A825' },
    { title: 'Groupeur (LCL)', desc: 'Partage de conteneur pour optimiser vos coûts.', icon: <GroupageIcon />, color: '#C62828' },
    { title: 'CAC Store', desc: 'Achetez des produits directement depuis la Chine.', icon: <StoreIcon />, color: '#6A1B9A' },
    { title: 'Tracking', desc: 'Suivi en temps réel de vos colis.', icon: <TrackingIcon />, color: '#E65100' }
];

const faqsData = [
    {
        question: 'Quels types de marchandises transportez-vous ?',
        answer: 'Nous transportons tous types de marchandises sauf les produits interdits (drogues, armes, matières dangereuses, etc.).'
    },
    {
        question: 'Comment puis-je suivre mon colis ?',
        answer: 'Vous pouvez suivre votre colis sur notre site web dans la section "Suivi de colis" en entrant votre numéro de suivi unique.'
    },
    {
        question: 'Proposez-vous une assurance ?',
        answer: 'Oui, nous proposons une assurance optionnelle couvrant la valeur de votre marchandise.'
    },
    {
        question: 'Livrez-vous à domicile ?',
        answer: 'Oui, nous proposons la livraison door-to-door dans tous nos pays de destination.'
    },
    {
        question: 'Comment contacter votre service client ?',
        answer: 'Vous pouvez nous contacter par WhatsApp, par email à contact@chineafriquecargo.com, ou via le formulaire de contact.'
    }
];

const valuesData = [
    { title: 'Fiabilité', desc: 'Votre marchandise arrive à destination en toute sécurité', icon: <SecurityIcon />, color: '#1565C0' },
    { title: 'Transparence', desc: 'Suivi en temps réel et communication claire', icon: <RealTimeIcon />, color: '#2E7D32' },
    { title: 'Réactivité', desc: 'Équipe disponible 7j/7 pour répondre à vos besoins', icon: <SpeedIcon />, color: '#F9A825' },
    { title: 'Proximité', desc: 'Des bureaux en Chine et dans 5 pays africains', icon: <PresenceIcon />, color: '#C62828' }
];

const API_URL = process.env.REACT_APP_API_URL || 'https://chineafriquecargo.com';
const WHATSAPP_CACHE_KEY = 'cac_whatsapp_number';
const WHATSAPP_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 heures

const About = () => {
    const [animated, setAnimated] = useState(false);
    const [whatsappNumber, setWhatsappNumber] = useState('+237 612345678');
    const [activeFaq, setActiveFaq] = useState(null);
    const [logoError, setLogoError] = useState(false);
    const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);

    useEffect(() => {
        setAnimated(true);
        fetchWhatsappNumber();
    }, []);

    const fetchWhatsappNumber = useCallback(async () => {
        // Vérifier le cache
        const cached = localStorage.getItem(WHATSAPP_CACHE_KEY);
        const cachedTime = localStorage.getItem(`${WHATSAPP_CACHE_KEY}_time`);

        if (cached && cachedTime && (Date.now() - parseInt(cachedTime)) < WHATSAPP_CACHE_DURATION) {
            setWhatsappNumber(cached);
            setLoadingWhatsapp(false);
            return;
        }

        try {
            const response = await axios.get(`${API_URL}/whatsapp/public/`);
            let numbers = [];
            if (response.data && response.data.results) {
                numbers = response.data.results;
            } else if (Array.isArray(response.data)) {
                numbers = response.data;
            }

            if (numbers.length === 0) {
                const defaultNumber = '+237 612345678';
                setWhatsappNumber(defaultNumber);
                localStorage.setItem(WHATSAPP_CACHE_KEY, defaultNumber);
                localStorage.setItem(`${WHATSAPP_CACHE_KEY}_time`, Date.now().toString());
            } else {
                const defaultNum = numbers.find(n => n.is_default) || numbers[0];
                setWhatsappNumber(defaultNum.phone_number);
                localStorage.setItem(WHATSAPP_CACHE_KEY, defaultNum.phone_number);
                localStorage.setItem(`${WHATSAPP_CACHE_KEY}_time`, Date.now().toString());
            }
        } catch (err) {
            console.error('Erreur chargement WhatsApp:', err);
            // Garder le numéro par défaut
        } finally {
            setLoadingWhatsapp(false);
        }
    }, []);

    const cleanPhoneNumber = (phone) => {
        return phone.replace(/\s+/g, '').replace(/[^0-9+]/g, '');
    };

    const handleLogoError = () => {
        setLogoError(true);
    };

    // Skeleton loader pour le chargement
    if (loadingWhatsapp) {
        return (
            <div className="skeleton-loader">
                <div className="skeleton-spinner"></div>
                <style>{`
                    .skeleton-loader {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
                    }
                    .skeleton-spinner {
                        width: 50px;
                        height: 50px;
                        border: 3px solid rgba(198,40,40,0.1);
                        border-top-color: #C62828;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="about-page">
            <Helmet>
                <title>CAC - À propos | Chine Afrique Cargo</title>
                <meta name="description" content="Depuis plus de 10 ans, CAC connecte les commerçants chinois aux marchés africains. Transport de marchandises entre la Chine et l'Afrique, fret aérien et maritime." />
                <meta name="keywords" content="transport chine afrique, logistique, fret aérien, fret maritime, CAC, groupage, dedouanement" />
                <meta name="author" content="CAC - Chine Afrique Cargo" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://chineafriquecargo.com/about" />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            </Helmet>

            {/* Hero Section Ultra Moderne */}
            <div className="about-hero">
                <div className="hero-overlay"></div>
                <Container className="hero-container">
                    <div className={`hero-content ${animated ? 'fade-in-up' : ''}`}>
                        <div className="hero-badge">
                            <span className="badge-pulse"> Qui sommes-nous</span>
                        </div>
                        <h1 className="hero-title">
                            À propos de <span className="text-gradient">CAC</span>
                        </h1>
                        <p className="hero-subtitle">
                            Chine Afrique Cargo – spécialiste du groupage entre la Chine et l’Afrique.
                        </p>
                    </div>
                </Container>
                <div className="hero-wave">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 64L60 69.3C120 75 240 85 360 80C480 75 600 53 720 48C840 43 960 53 1080 58.7C1200 64 1320 64 1380 64L1440 64L1440 120L1380 120C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120L0 120Z" fill="#f8f9fa" />
                    </svg>
                </div>
            </div>

            {/* Histoire Section avec Image */}
            <div className="history-section">
                <Container>
                    <Row className="align-items-center g-5">
                        <Col lg={6}>
                            <div className="history-image">
                                <div className="image-badge">
                                    <span>10+ ans</span>
                                </div>
                                <div className="image-content">
                                    {!logoError ? (
                                        <img
                                            src={logo}
                                            alt="CAC Logo"
                                            className="history-logo"
                                            loading="lazy"
                                            onError={handleLogoError}
                                        />
                                    ) : (
                                        <div className="logo-fallback">🚚</div>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className="section-header text-start">
                                <span className="section-badge">Notre Histoire</span>
                                <h2 className="section-title">Depuis plus de <span className="text-gradient">10 ans</span> à vos côtés</h2>
                                <p className="section-subtitle">
                                    Chine Afrique Cargo (C.A.C) est une entreprise spécialisée dans la logistique entre la Chine et l’Afrique.
                                </p>
                            </div>
                            <div className="history-text">
                                <p>
                                    Nous aidons les importateurs à transporter leurs marchandises de manière économique,
                                    rapide et sécurisée, même pour de petits volumes.
                                </p>
                                <p>
                                    Aujourd'hui, nous sommes présents dans 5 pays africains et avons transporté
                                    plus de 5000 colis avec un taux de satisfaction client de 99%.
                                </p>
                            </div>
                            <div className="mission-card">
                                <div className="mission-icon">🎯</div>
                                <div className="mission-content">
                                    <h5>Notre mission</h5>
                                    <p>
                                        Rendre l’importation depuis la Chine simple, accessible et rentable,
                                        grâce à des solutions de groupage fiables en fret maritime et aérien.
                                    </p>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Stats Section avec icônes SVG */}
            <div className="stats-section">
                <Container>
                    <Row className="g-4">
                        {statsData.map((stat, idx) => (
                            <Col md={3} sm={6} key={idx}>
                                <div className="stat-card" style={{ animationDelay: `${stat.delay}ms` }}>
                                    <div className="stat-icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                                        {stat.icon}
                                    </div>
                                    <div className="stat-value">{stat.value}</div>
                                    <div className="stat-label">{stat.label}</div>
                                    <div className="stat-line" style={{ backgroundColor: stat.color }}></div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>

            {/* Services Section avec icônes SVG */}
            <div className="services-section">
                <Container>
                    <div className="section-header text-center">
                        <span className="section-badge">Nos Services</span>
                        <h2 className="section-title">Des solutions <span className="text-gradient">complètes</span></h2>
                        <p className="section-subtitle">Une gamme de services adaptée à tous vos besoins logistiques</p>
                    </div>
                    <Row className="g-4">
                        {servicesData.map((service, idx) => (
                            <Col md={4} lg={4} key={idx}>
                                <div className="service-card">
                                    <div className="service-icon" style={{ backgroundColor: `${service.color}15`, color: service.color }}>
                                        {service.icon}
                                    </div>
                                    <h5 className="service-title">{service.title}</h5>
                                    <p className="service-desc">{service.desc}</p>
                                    <div className="service-hover-line" style={{ backgroundColor: service.color }}></div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>

            {/* Valeurs Section avec icônes SVG */}
            <div className="values-section">
                <Container>
                    <div className="section-header text-center">
                        <span className="section-badge">Nos Valeurs</span>
                        <h2 className="section-title">Ce qui nous <span className="text-gradient">anime</span></h2>
                    </div>
                    <Row className="g-4">
                        {valuesData.map((value, idx) => (
                            <Col md={3} sm={6} key={idx}>
                                <div className="value-card">
                                    <div className="value-icon" style={{ backgroundColor: `${value.color}15`, color: value.color }}>
                                        {value.icon}
                                    </div>
                                    <h5 className="value-title">{value.title}</h5>
                                    <p className="value-desc">{value.desc}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>

            {/* Présence Section */}
            <div className="presence-section">
                <Container>
                    <div className="section-header text-center">
                        <span className="section-badge">Notre Présence</span>
                        <h2 className="section-title">Implantés en <span className="text-gradient">Chine et en Afrique</span></h2>
                    </div>
                    <Row className="g-4">
                        <Col md={6}>
                            <div className="presence-card china">
                                <div className="presence-header">
                                    <span className="presence-flag">🇨🇳</span>
                                    <h3>Chine (Siège)</h3>
                                </div>
                                <div className="presence-content">
                                    <p><strong>📍 Adresse :</strong></p>
                                    <p className="address">
                                        佛山市南海区里水镇湖州村湖州大道10号<br />
                                        (导航：锦丰压花冲孔)
                                    </p>
                                    <p><strong>📞 Contact :</strong> {whatsappNumber} (WeChat/WhatsApp)</p>
                                    <p><strong>⏰ Horaires :</strong> Lun-Sam: 9:30-20:00 | Dim: 9:30-18:00</p>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="presence-card africa">
                                <div className="presence-header">
                                    <span className="presence-flag">🌍</span>
                                    <h3>Bureaux Afrique</h3>
                                </div>
                                <div className="presence-content">
                                    <Row>
                                        <Col sm={6}>
                                            <p><strong>🇬🇦 Gabon :</strong><br />+24107385957</p>
                                            <p><strong>🇲🇱 Mali :</strong><br />+22383916131<br />72021696</p>
                                        </Col>
                                        <Col sm={6}>
                                            <p><strong>🇨🇬 Congo :</strong><br />+242053949494<br />+242068388383</p>
                                            <p><strong>🇨🇮 Côte d\'Ivoire :</strong><br />+2250758581155<br />+2250748478057</p>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* FAQ Section interactive */}
            <div className="faq-section">
                <Container>
                    <div className="section-header text-center">
                        <span className="section-badge">FAQ</span>
                        <h2 className="section-title">Questions <span className="text-gradient">fréquentes</span></h2>
                    </div>
                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <div className="faq-container">
                                {faqsData.map((faq, idx) => (
                                    <div key={idx} className="faq-item">
                                        <div
                                            className="faq-question"
                                            onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyPress={(e) => e.key === 'Enter' && setActiveFaq(activeFaq === idx ? null : idx)}
                                        >
                                            <span className="faq-icon">{activeFaq === idx ? '▼' : '▶'}</span>
                                            <h6>{faq.question}</h6>
                                        </div>
                                        {activeFaq === idx && (
                                            <div className="faq-answer">
                                                <p>{faq.answer}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
                <Container className="text-center">
                    <h2 className="cta-title">Prêt à expédier ?</h2>
                    <p className="cta-subtitle">
                        Contactez-nous dès maintenant pour un devis gratuit et personnalisé
                    </p>
                    <div className="cta-buttons">
                        <a
                            href={`https://wa.me/${cleanPhoneNumber(whatsappNumber)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-cta-whatsapp"
                        >
                            💬 WhatsApp
                        </a>
                        <Link to="/contact" className="btn-cta-primary">
                            📧 Nous contacter
                        </Link>
                    </div>
                </Container>
            </div>

            <style>{`
                .about-page {
                    background: #f8f9fa;
                    overflow-x: hidden;
                }

                /* Hero Section */
                .about-hero {
                    position: relative;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                    min-height: 450px;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                }

                .hero-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: radial-gradient(circle at 20% 50%, rgba(198, 40, 40, 0.2) 0%, transparent 50%);
                }

                .hero-container {
                    position: relative;
                    z-index: 2;
                    padding: 80px 0;
                }

                .hero-content {
                    text-align: center;
                    max-width: 700px;
                    margin: 0 auto;
                }

                .hero-badge {
                    margin-bottom: 24px;
                }

                .badge-pulse {
                    display: inline-block;
                    padding: 8px 20px;
                    background: rgba(249, 168, 37, 0.15);
                    border: 1px solid rgba(249, 168, 37, 0.3);
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #F9A825;
                    animation: pulse 2s infinite;
                }

                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(249, 168, 37, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0); }
                }

                .hero-title {
                    font-size: 56px;
                    font-weight: 800;
                    margin-bottom: 20px;
                    color: white;
                }

                .text-gradient {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .hero-subtitle {
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.7);
                }

                .hero-wave {
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    right: 0;
                }

                /* History Section */
                .history-section {
                    padding: 80px 0;
                    background: white;
                }

                .history-image {
                    position: relative;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border-radius: 30px;
                    padding: 40px;
                    min-height: 400px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .history-logo {
                    max-width: 80%;
                    max-height: 280px;
                    object-fit: contain;
                    border-radius: 20px;
                    transition: transform 0.3s ease;
                }

                .history-logo:hover {
                    transform: scale(1.02);
                }

                .logo-fallback {
                    font-size: 80px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .image-badge {
                    position: absolute;
                    top: -20px;
                    right: -20px;
                    background: white;
                    border-radius: 20px;
                    padding: 12px 24px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                }

                .image-badge span {
                    font-size: 18px;
                    font-weight: 800;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .image-content {
                    text-align: center;
                }

                .history-text p {
                    color: #666;
                    line-height: 1.8;
                    margin-bottom: 20px;
                }

                .mission-card {
                    background: linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%);
                    border-radius: 16px;
                    padding: 20px;
                    display: flex;
                    gap: 16px;
                    margin-top: 30px;
                }

                .mission-icon {
                    font-size: 32px;
                }

                .mission-content h5 {
                    font-weight: 700;
                    margin-bottom: 8px;
                    color: #C62828;
                }

                .mission-content p {
                    margin: 0;
                    color: #666;
                }

                /* Stats Section */
                .stats-section {
                    padding: 60px 0;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                }

                .stat-card {
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(10px);
                    border-radius: 20px;
                    padding: 30px;
                    text-align: center;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    animation: fadeInUp 0.6s ease forwards;
                    opacity: 0;
                }

                .stat-card:hover {
                    transform: translateY(-8px);
                    background: rgba(255, 255, 255, 0.1);
                }

                .stat-icon {
                    width: 60px;
                    height: 60px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                    transition: transform 0.3s ease;
                }

                .stat-card:hover .stat-icon svg {
                    transform: scale(1.1);
                }

                .stat-value {
                    font-size: 36px;
                    font-weight: 800;
                    color: white;
                    margin-bottom: 8px;
                }

                .stat-label {
                    font-size: 14px;
                    color: rgba(255, 255, 255, 0.7);
                }

                .stat-line {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    transform: scaleX(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover .stat-line {
                    transform: scaleX(1);
                }

                /* Services Section */
                .services-section {
                    padding: 80px 0;
                    background: white;
                }

                .section-header {
                    margin-bottom: 50px;
                }

                .section-badge {
                    display: inline-block;
                    padding: 6px 16px;
                    background: rgba(198, 40, 40, 0.1);
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #C62828;
                    margin-bottom: 16px;
                }

                .section-title {
                    font-size: 40px;
                    font-weight: 800;
                    margin-bottom: 16px;
                }

                .section-subtitle {
                    font-size: 18px;
                    color: #666;
                }

                .service-card {
                    background: #f8f9fa;
                    border-radius: 20px;
                    padding: 30px;
                    text-align: center;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    height: 100%;
                }

                .service-card:hover {
                    transform: translateY(-8px);
                    background: white;
                    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
                }

                .service-icon {
                    width: 70px;
                    height: 70px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    transition: transform 0.3s ease;
                }

                .service-card:hover .service-icon svg {
                    transform: scale(1.1);
                }

                .service-title {
                    font-size: 18px;
                    font-weight: 700;
                    margin-bottom: 12px;
                }

                .service-desc {
                    font-size: 14px;
                    color: #666;
                    margin: 0;
                }

                .service-hover-line {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    transform: scaleX(0);
                    transition: transform 0.3s ease;
                }

                .service-card:hover .service-hover-line {
                    transform: scaleX(1);
                }

                /* Values Section */
                .values-section {
                    padding: 80px 0;
                    background: #f8f9fa;
                }

                .value-card {
                    background: white;
                    border-radius: 16px;
                    padding: 30px;
                    text-align: center;
                    transition: all 0.3s ease;
                }

                .value-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                }

                .value-icon {
                    width: 70px;
                    height: 70px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 20px;
                    transition: transform 0.3s ease;
                }

                .value-card:hover .value-icon svg {
                    transform: scale(1.1);
                }

                .value-title {
                    font-size: 18px;
                    font-weight: 700;
                    margin-bottom: 12px;
                }

                .value-desc {
                    font-size: 14px;
                    color: #666;
                    margin: 0;
                }

                /* Presence Section */
                .presence-section {
                    padding: 80px 0;
                    background: white;
                }

                .presence-card {
                    background: #f8f9fa;
                    border-radius: 24px;
                    padding: 30px;
                    transition: all 0.3s ease;
                    height: 100%;
                }

                .presence-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
                }

                .presence-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 20px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #e0e0e0;
                }

                .presence-flag {
                    font-size: 32px;
                }

                .presence-header h3 {
                    margin: 0;
                    font-weight: 700;
                }

                .presence-content p {
                    margin-bottom: 12px;
                }

                .address {
                    color: #666;
                    line-height: 1.6;
                }

                /* FAQ Section */
                .faq-section {
                    padding: 80px 0;
                    background: #f8f9fa;
                }

                .faq-container {
                    background: white;
                    border-radius: 24px;
                    overflow: hidden;
                }

                .faq-item {
                    border-bottom: 1px solid #eee;
                }

                .faq-item:last-child {
                    border-bottom: none;
                }

                .faq-question {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 20px 24px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .faq-question:hover {
                    background: #f8f9fa;
                }

                .faq-icon {
                    font-size: 14px;
                    color: #C62828;
                    font-weight: bold;
                }

                .faq-question h6 {
                    margin: 0;
                    font-weight: 600;
                }

                .faq-answer {
                    padding: 0 24px 20px 60px;
                    color: #666;
                    line-height: 1.6;
                }

                /* CTA Section */
                .cta-section {
                    padding: 80px 0;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                }

                .cta-title {
                    font-size: 40px;
                    font-weight: 800;
                    color: white;
                    margin-bottom: 16px;
                }

                .cta-subtitle {
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 32px;
                }

                .cta-buttons {
                    display: flex;
                    gap: 16px;
                    justify-content: center;
                    flex-wrap: wrap;
                }

                .btn-cta-whatsapp {
                    background: #25D366;
                    border: none;
                    padding: 14px 32px;
                    border-radius: 50px;
                    font-weight: 600;
                    color: white;
                    text-decoration: none;
                    transition: all 0.3s ease;
                }

                .btn-cta-whatsapp:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(37, 211, 102, 0.4);
                }

                .btn-cta-primary {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    padding: 14px 32px;
                    border-radius: 50px;
                    font-weight: 600;
                    color: white;
                    text-decoration: none;
                    transition: all 0.3s ease;
                }

                .btn-cta-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(198, 40, 40, 0.4);
                }

                /* Animations */
                .fade-in-up {
                    animation: fadeInUp 0.8s ease forwards;
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Responsive */
                @media (max-width: 992px) {
                    .hero-title {
                        font-size: 40px;
                    }
                    
                    .section-title {
                        font-size: 32px;
                    }
                    
                    .cta-title {
                        font-size: 32px;
                    }
                    
                    .service-icon, .value-icon, .stat-icon {
                        width: 55px;
                        height: 55px;
                    }
                }
                
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 32px;
                    }
                    
                    .section-title {
                        font-size: 28px;
                    }
                    
                    .stat-value {
                        font-size: 28px;
                    }
                    
                    .faq-question {
                        flex-direction: column;
                        text-align: center;
                    }
                    
                    .faq-answer {
                        padding: 0 20px 20px 20px;
                        text-align: center;
                    }
                    
                    .history-logo {
                        max-width: 90%;
                    }
                    
                    .service-icon, .value-icon, .stat-icon {
                        width: 45px;
                        height: 45px;
                    }
                    
                    .service-icon svg, .value-icon svg, .stat-icon svg {
                        width: 28px;
                        height: 28px;
                    }
                }
            `}</style>
        </div>
    );
};

export default About;